/**
 * Wire shapes for the inbox read model (stream-chat-backend
 * `POST /api/chat/inbox/{query,facets}`). Shared so the BFF validates exactly
 * the shape the client builds.
 *
 * `viewer_id` is deliberately ABSENT from the client-facing bodies. The
 * upstream takes a caller-asserted viewer id on a shared service bearer, so
 * the BFF stamps it from the verified session and `parseInboxQueryBody` drops
 * any the client sent. Identity never comes from request input.
 */

import { GENDERS, type Gender } from './api-types.js';

/** One filter clause, mirroring the backend's `InboxFilter` discriminated shape. */
export type InboxFilterClause =
  | { kind: 'status'; phases: string[] }
  /**
   * `unread` is the one the inbox's pill means. `need-reply` is the wider,
   * older cut — every chat whose last message is someone else's, read or not.
   */
  | { kind: 'scope'; value: 'need-reply' | 'unread' }
  | { kind: 'kind'; kinds: string[] }
  | { kind: 'job'; job_ids: string[] }
  | { kind: 'counterpart'; counterpart_ids: string[] }
  | { kind: 'application_status'; stages: string[] }
  | { kind: 'source'; sources: string[] }
  | { kind: 'job_location'; locations: string[] }
  /**
   * `any` also matches message bodies, which the read model does not index: the
   * BFF resolves that half against Stream and fills `channel_ids` in, so the
   * client neither sends nor sees them.
   */
  | { kind: 'search'; field: 'name' | 'any'; text: string; channel_ids?: string[] }
  /**
   * The candidate clauses. These read the *person* on the other side rather
   * than the conversation or its posting, and none has a column on the inbox
   * read model — upstream resolves them against the candidate index and folds
   * them back into its own person predicate before any SQL is compiled.
   *
   * `preferred_location` is the candidate's own stated preference and is a
   * different question from `job_location`, which is the posting's city. Both
   * stay on the wire: the employer dialog offers only the former, but a saved
   * view made before this existed still carries the latter.
   */
  | { kind: 'skills'; skills: string[] }
  | { kind: 'languages'; languages: string[] }
  | { kind: 'education'; education: string[] }
  | { kind: 'preferred_location'; preferred_locations: string[] }
  | { kind: 'nationality'; nationalities: string[] }
  | { kind: 'availability'; availabilities: string[] }
  | { kind: 'gender'; genders: Gender[] }
  /** Inclusive whole years, worked out upstream from the birthdate. */
  | { kind: 'age'; age_min?: number; age_max?: number }
  /** Inclusive bounds in whole MYR. At least one side is always present. */
  | { kind: 'expected_salary'; salary_min?: number; salary_max?: number };

/** The only ordering the read model's keyset indexes serve. */
export interface InboxSort {
  key: 'last_message_at';
  dir: 'desc';
}

export interface InboxQueryBody {
  filters: InboxFilterClause[];
  cursor?: string;
  limit?: number;
}

export const INBOX_FACET_DIMS = [
  'job',
  'person',
  'application_status',
  'source',
  'kind',
  'job_location',
  // Counted upstream from resolved candidate facts rather than by a GROUP BY,
  // which is why these six can be dimensions at all — nothing in the inbox
  // read model holds them.
  'skills',
  'languages',
  'education',
  'preferred_location',
  'nationality',
  'availability',
  'gender',
  'expected_salary',
] as const;

export type InboxFacetDim = (typeof INBOX_FACET_DIMS)[number];

export interface InboxFacetsBody extends InboxQueryBody {
  by: InboxFacetDim;
}

export interface InboxQueryResult {
  channel_ids: string[];
  /**
   * Rides on this response rather than on the channel because Stream's channel
   * custom data is readable by every member, the jobseeker included. Absent keeps
   * meaning "no marks" rather than "no answer".
   */
  assistant_flagged?: string[];
  /**
   * Each job chat's hiring stage on this page, by channel id: a hint from the
   * read model so a thread can draw its stage controls on open. Employer only,
   * for the same reason as `assistant_flagged`; `/details` still has the last word.
   */
  application_statuses?: { channel_id: string; status: string }[];
  /** The subset of `channel_ids` whose candidate this employer has unlocked.
   *  Absent means unknown, not "all locked". */
  profile_unlocked_channels?: string[];
  next_cursor: string;
  total: number;
  /**
   * Set when Stream could not answer the body half, so these rows are name
   * matches only. Added by the BFF on the way out; the read model knows
   * nothing about it.
   */
  message_search_unavailable?: boolean;
  /**
   * The counterparts a body search matched through their profile — the
   * Candidates section's membership. Absent when no profile matched. Stable
   * across the pages of one search (memoised upstream with the rest of the
   * resolution), so the section cannot shift under a cursor.
   */
  profile_matched_counterparts?: string[];
}

export interface InboxFacet {
  key: string;
  label: string;
  count: number;
  /**
   * The job family this facet belongs to. Set on `by: 'job'` only, and only for
   * a sub-role — a standalone job and every other dimension omit both.
   *
   * snake_case because these are the read model's own field names, carried
   * through the BFF verbatim like the rest of this shape.
   */
  parent_key?: string;
  parent_label?: string;
  /** Where the posting runs (`meta.job_location`'s value). `by: 'job'` only. */
  location?: string;
}

export interface InboxFacetsResult {
  facets: InboxFacet[];
  next_cursor: string;
  /**
   * Conversations whose candidate states nothing on this dimension. Candidate
   * dimensions only; the column-backed ones omit it.
   *
   * It is not a spare statistic. Coverage in the index is low — skills sit
   * around 28% — and a candidate with no value drops out the moment a pick is
   * in force, so ticking one skill can take seven conversations in ten off the
   * list. `count` says how many rows a pick keeps; this says how many had no
   * opinion either way. A row that shows the first without the second reads as
   * "you have almost nobody" when the truth is "we do not know about most of
   * them".
   */
  unknown_count?: number;
  /**
   * The candidate set was too large to resolve in full, so the counts are a
   * floor rather than the answer. Surfaced, never swallowed: a quietly short
   * list is worse than one that admits it is short.
   */
  truncated?: boolean;
}

/** Thrown for a body the read model could not honour — the route maps it to 400. */
export class InboxQueryShapeError extends Error {}

/** The upstream's own caps, mirrored so a bad body fails at our edge with a
 *  clear message instead of coming back as an opaque 422. */
const MAX_FILTERS = 20;
const MAX_LIMIT = 100;

function stringList(value: unknown, field: string): string[] {
  if (!Array.isArray(value) || value.length === 0 || value.some((v) => typeof v !== 'string')) {
    throw new InboxQueryShapeError(`${field} must be a non-empty array of strings`);
  }
  return value as string[];
}

/**
 * A non-empty list narrowed to a closed vocabulary, matched case-insensitively
 * and returned in the vocabulary's own casing.
 *
 * An unrecognised value is refused rather than dropped. Dropping compiles to a
 * clause that matches nothing and reports it as an empty inbox — the employer
 * reads "you have no conversations" where the truth is "that is not a thing you
 * can filter by", and nothing on screen tells the two apart.
 */
function closedSet<T extends string>(value: unknown, allowed: readonly T[], field: string): T[] {
  return stringList(value, field).map((v) => {
    const match = allowed.find((option) => option.toLowerCase() === v.toLowerCase());
    if (!match) throw new InboxQueryShapeError(`unknown ${field} ${JSON.stringify(v)}`);
    return match;
  });
}

function parseClause(input: unknown): InboxFilterClause {
  const c = (input ?? {}) as Record<string, unknown>;
  switch (c.kind) {
    case 'status':
      return { kind: 'status', phases: stringList(c.phases, 'phases') };
    case 'scope':
      if (c.value !== 'need-reply' && c.value !== 'unread') {
        throw new InboxQueryShapeError(`unknown scope value ${String(c.value)}`);
      }
      return { kind: 'scope', value: c.value };
    case 'kind':
      return { kind: 'kind', kinds: stringList(c.kinds, 'kinds') };
    case 'job':
      return { kind: 'job', job_ids: stringList(c.job_ids, 'job_ids') };
    case 'counterpart':
      return {
        kind: 'counterpart',
        counterpart_ids: stringList(c.counterpart_ids, 'counterpart_ids'),
      };
    case 'application_status':
      return { kind: 'application_status', stages: stringList(c.stages, 'stages') };
    case 'source':
      return { kind: 'source', sources: stringList(c.sources, 'sources') };
    case 'job_location':
      return { kind: 'job_location', locations: stringList(c.locations, 'locations') };
    case 'search': {
      // `channel_ids` is never read from input: the BFF resolves the body half
      // against Stream and fills it in, so a client cannot inject a channel set.
      const field = c.field === 'name' || c.field === 'any' ? c.field : null;
      if (!field || typeof c.text !== 'string' || c.text === '') {
        throw new InboxQueryShapeError('search requires field "name" or "any" and non-empty text');
      }
      return { kind: 'search', field, text: c.text };
    }
    // Candidate clauses. The closed sets are checked here rather than passed
    // through, so a value outside the ladder fails at our edge with a message
    // instead of upstream with a 422 the client cannot explain.
    case 'skills':
      return { kind: 'skills', skills: stringList(c.skills, 'skills') };
    case 'languages':
      return { kind: 'languages', languages: stringList(c.languages, 'languages') };
    case 'education':
      return {
        kind: 'education',
        education: closedSet(c.education, EDUCATION_LEVELS, 'education level'),
      };
    case 'preferred_location':
      return {
        kind: 'preferred_location',
        preferred_locations: stringList(c.preferred_locations, 'preferred_locations'),
      };
    case 'nationality':
      return {
        kind: 'nationality',
        nationalities: closedSet(c.nationalities, NATIONALITIES, 'nationality'),
      };
    case 'availability':
      return {
        kind: 'availability',
        availabilities: closedSet(c.availabilities, AVAILABILITIES, 'availability'),
      };
    case 'gender':
      return { kind: 'gender', genders: closedSet(c.genders, GENDERS, 'gender') };
    case 'age': {
      const ageMin = optionalAge(c.age_min, 'age_min');
      const ageMax = optionalAge(c.age_max, 'age_max');
      if (ageMin === undefined && ageMax === undefined) {
        throw new InboxQueryShapeError('age needs age_min or age_max');
      }
      if (ageMin !== undefined && ageMax !== undefined && ageMin > ageMax) {
        throw new InboxQueryShapeError('age_min must not exceed age_max');
      }
      return {
        kind: 'age',
        ...(ageMin === undefined ? {} : { age_min: ageMin }),
        ...(ageMax === undefined ? {} : { age_max: ageMax }),
      };
    }
    case 'expected_salary': {
      const salaryMin = optionalSalary(c.salary_min, 'salary_min');
      const salaryMax = optionalSalary(c.salary_max, 'salary_max');
      // An unbounded salary clause is not "everyone", it is a clause that
      // should never have been sent. Refusing it keeps the compiler's rule that
      // an untouched row never reaches the wire.
      if (salaryMin === undefined && salaryMax === undefined) {
        throw new InboxQueryShapeError('expected_salary needs salary_min or salary_max');
      }
      if (salaryMin !== undefined && salaryMax !== undefined && salaryMin > salaryMax) {
        throw new InboxQueryShapeError('salary_min must not exceed salary_max');
      }
      return {
        kind: 'expected_salary',
        ...(salaryMin === undefined ? {} : { salary_min: salaryMin }),
        ...(salaryMax === undefined ? {} : { salary_max: salaryMax }),
      };
    }
    default:
      throw new InboxQueryShapeError(`unknown filter kind ${String(c.kind)}`);
  }
}

function parseCommon(input: unknown): InboxQueryBody {
  const b = (input ?? {}) as Record<string, unknown>;
  const raw = b.filters ?? [];
  if (!Array.isArray(raw)) throw new InboxQueryShapeError('filters must be an array');
  if (raw.length > MAX_FILTERS) throw new InboxQueryShapeError('too many filters');

  const out: InboxQueryBody = { filters: raw.map(parseClause) };
  if (typeof b.cursor === 'string' && b.cursor !== '') out.cursor = b.cursor;
  if (typeof b.limit === 'number' && Number.isFinite(b.limit)) {
    out.limit = Math.min(MAX_LIMIT, Math.max(1, Math.trunc(b.limit)));
  }
  return out;
}

/** Parse a client `POST /api/inbox/query` body. Throws InboxQueryShapeError. */
export function parseInboxQueryBody(input: unknown): InboxQueryBody {
  return parseCommon(input);
}

/** Parse a client `POST /api/inbox/facets` body. Throws InboxQueryShapeError. */
export function parseInboxFacetsBody(input: unknown): InboxFacetsBody {
  const by = (input as { by?: unknown } | null | undefined)?.by;
  if (typeof by !== 'string' || !(INBOX_FACET_DIMS as readonly string[]).includes(by)) {
    throw new InboxQueryShapeError(`unknown facet dimension ${String(by)}`);
  }
  return { ...parseCommon(input), by: by as InboxFacetDim };
}

// ── candidate filters ────────────────────────────────────────────────────────
/**
 * The candidate-side funnel: filters that read the *person* who applied rather
 * than the posting they applied to.
 *
 * Everything in the inbox read model describes a job — which posting, open
 * where, how far along. None of it describes the applicant, and none of these
 * five is a column on it. They live in the ES jobseekers index, keyed on the
 * jobseeker; the inbox is keyed on a Stream member id. The BFF bridges the two
 * and compiles a pick down to the `counterpart` clause above, which is why this
 * vocabulary is here rather than in the wire shapes: the read model never sees
 * it.
 *
 * `preferredLocation` is the candidate's, NOT the posting's. The two are the
 * most confusable pair on the surface, and `job_location` above is the other
 * one — a posting's city is already printed on every row, so filtering by it
 * tells an employer something they can see.
 */

/**
 * How far somebody got in school, as a closed ladder rather than free text.
 *
 * These five and their wording are the host's applicant manager's, copied
 * rather than designed: an employer who screened a posting there and then opens
 * the chat has to be offered the same rungs, or the two surfaces disagree about
 * how many graduates applied. So "Degree" and not "Bachelor's Degree", and
 * "Master / PHD" as one rung rather than two.
 *
 * **The order is the meaning.** A filter offering rungs has to list them in the
 * order they are climbed; sorted by name it would file Diploma above Degree and
 * read as nonsense. This array is the only place that order is written down.
 *
 * The index stores something else again — a lowercased free-text qualification
 * — so the BFF maps onto this ladder on the way through. That map is not here:
 * it is a fact about one upstream index, and this is the vocabulary two of our
 * own workspaces have to agree on.
 */
export const EDUCATION_LEVELS = [
  'Primary School',
  'Secondary School',
  'Diploma',
  'Degree',
  'Master / PHD',
] as const;

export type EducationLevel = (typeof EDUCATION_LEVELS)[number];

/** Malaysian or not, as the index states it on 99.9% of production documents.
 *  Shared so the details panel can never show a value the filter cannot offer;
 *  the host's third value, "PR/Work permit holder", is not in the index. */
export const NATIONALITIES = ['Malaysian', 'Non-Malaysian'] as const;

export type Nationality = (typeof NATIONALITIES)[number];

/** How soon they could start, soonest first. Upstream folds the index's
 *  spellings onto these rungs. */
export const AVAILABILITIES = [
  'Immediately',
  'After 1 week',
  'After 1 month',
  'After 2 months',
  'After 3 months',
  'Flexible',
] as const;

export type Availability = (typeof AVAILABILITIES)[number];

/** The plausible ages upstream accepts, the same window the details panel uses. */
export const AGE_MIN = 14;
export const AGE_MAX = 100;

/**
 * The part of an age range that is a real cut: a half-typed bound ("2" on the
 * way to "25") or crossed bounds are not one yet, and upstream would refuse them.
 */
export function ageBounds({ min, max }: { min?: number; max?: number }): {
  min?: number;
  max?: number;
} {
  const inRange = (v?: number) => (v !== undefined && v >= AGE_MIN && v <= AGE_MAX ? v : undefined);
  const lo = inRange(min);
  const hi = inRange(max);
  if (lo !== undefined && hi !== undefined && lo > hi) return {};
  return { ...(lo === undefined ? {} : { min: lo }), ...(hi === undefined ? {} : { max: hi }) };
}

/**
 * What the employer picked. Every list is OR within itself and AND against the
 * others, and an empty one is no cut at all — the same contract the clause
 * compilers above keep, so an untouched row never narrows anything.
 */
export interface CandidateCriteria {
  skills: string[];
  languages: string[];
  education: EducationLevel[];
  /** Cities the candidate says they want to work in. Never the posting's. */
  preferredLocations: string[];
  nationalities: Nationality[];
  /** Inclusive bounds, in whole MYR. Absent means unbounded on that side. */
  salaryMin?: number;
  salaryMax?: number;
}

/** Nothing picked. The value a fresh funnel holds. */
export function emptyCandidateCriteria(): CandidateCriteria {
  return { skills: [], languages: [], education: [], preferredLocations: [], nationalities: [] };
}

/** Whether anything here would narrow the list. */
export function candidateCriteriaSet(criteria: CandidateCriteria): boolean {
  return (
    criteria.skills.length > 0 ||
    criteria.languages.length > 0 ||
    criteria.education.length > 0 ||
    criteria.preferredLocations.length > 0 ||
    criteria.nationalities.length > 0 ||
    criteria.salaryMin !== undefined ||
    criteria.salaryMax !== undefined
  );
}

/**
 * The criteria as wire clauses, one per dimension the employer touched.
 *
 * A dimension nobody picked contributes nothing, which is what keeps an
 * untouched row from narrowing anything. Clauses AND upstream, so returning
 * several is the same as one compound cut.
 *
 * There is no id resolution and no second round trip: these ride on the query
 * the client already sends, and the backend joins them to the candidate index
 * itself. The BFF match endpoint this once needed is gone.
 */
export function candidateClauses(criteria: CandidateCriteria): InboxFilterClause[] {
  const clauses: InboxFilterClause[] = [];
  if (criteria.skills.length > 0) clauses.push({ kind: 'skills', skills: criteria.skills });
  if (criteria.languages.length > 0) {
    clauses.push({ kind: 'languages', languages: [...criteria.languages] });
  }
  if (criteria.education.length > 0) {
    clauses.push({ kind: 'education', education: [...criteria.education] });
  }
  if (criteria.preferredLocations.length > 0) {
    clauses.push({ kind: 'preferred_location', preferred_locations: criteria.preferredLocations });
  }
  if (criteria.nationalities.length > 0) {
    clauses.push({ kind: 'nationality', nationalities: [...criteria.nationalities] });
  }
  if (criteria.salaryMin !== undefined || criteria.salaryMax !== undefined) {
    clauses.push({
      kind: 'expected_salary',
      ...(criteria.salaryMin === undefined ? {} : { salary_min: criteria.salaryMin }),
      ...(criteria.salaryMax === undefined ? {} : { salary_max: criteria.salaryMax }),
    });
  }
  return clauses;
}

/** The dimensions a candidate clause can carry, for stripping and regrouping. */
export const CANDIDATE_FILTER_KINDS = [
  'skills',
  'languages',
  'education',
  'preferred_location',
  'nationality',
  'expected_salary',
] as const;

export type CandidateFilterKind = (typeof CANDIDATE_FILTER_KINDS)[number];

/** Whether a clause reads the candidate rather than the conversation. */
export function isCandidateClause(clause: InboxFilterClause): boolean {
  return (CANDIDATE_FILTER_KINDS as readonly string[]).includes(clause.kind);
}

/** Per-row pick cap. A funnel row nobody could have filled by hand. */
const MAX_CRITERIA_VALUES = 50;
/** Longest single value. The index's own keyword fields stop well short. */
const MAX_CRITERIA_VALUE_LEN = 120;
/** Above this a salary is a typo or a troll, not an expectation. */
const MAX_SALARY = 1_000_000;

/**
 * An optional list of free text. Absent and empty both mean "no cut", so unlike
 * `stringList` above an empty array is accepted — the caller drops it rather
 * than sending an empty `IN`.
 */
function optionalStrings(value: unknown, field: string): string[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) throw new InboxQueryShapeError(`${field} must be an array`);
  if (value.length > MAX_CRITERIA_VALUES) {
    throw new InboxQueryShapeError(`${field} accepts at most ${MAX_CRITERIA_VALUES} values`);
  }
  return value.map((v) => {
    if (typeof v !== 'string' || v.trim() === '') {
      throw new InboxQueryShapeError(`${field} must hold non-empty strings`);
    }
    if (v.length > MAX_CRITERIA_VALUE_LEN) {
      throw new InboxQueryShapeError(`a ${field} value is too long`);
    }
    return v.trim();
  });
}

/**
 * The same, narrowed to a closed set.
 *
 * A value outside it is refused rather than dropped. Dropping would compile to
 * a filter that matches nothing and report it as an empty inbox — the employer
 * reads "you have no conversations" where the truth is "that is not a thing you
 * can filter by", and nothing on screen tells them apart.
 */
function fromClosedSet<T extends string>(
  value: unknown,
  allowed: readonly T[],
  field: string,
): T[] {
  return optionalStrings(value, field).map((v) => {
    const match = allowed.find((option) => option.toLowerCase() === v.toLowerCase());
    if (!match) throw new InboxQueryShapeError(`unknown ${field} ${JSON.stringify(v)}`);
    return match;
  });
}

/** An optional whole-year age bound. */
function optionalAge(value: unknown, field: string): number | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'number' || !Number.isInteger(value) || value < AGE_MIN || value > AGE_MAX) {
    throw new InboxQueryShapeError(`${field} must be a whole number from ${AGE_MIN} to ${AGE_MAX}`);
  }
  return value;
}

/** An optional whole-MYR bound. */
function optionalSalary(value: unknown, field: string): number | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new InboxQueryShapeError(`${field} must be a number`);
  }
  const amount = Math.trunc(value);
  if (amount < 0 || amount > MAX_SALARY) {
    throw new InboxQueryShapeError(`${field} must be between 0 and ${MAX_SALARY}`);
  }
  return amount;
}

/** Parse the criteria half of a match request. Throws InboxQueryShapeError. */
export function parseCandidateCriteria(input: unknown): CandidateCriteria {
  const c = (input ?? {}) as Record<string, unknown>;
  const salaryMin = optionalSalary(c.salaryMin, 'salaryMin');
  const salaryMax = optionalSalary(c.salaryMax, 'salaryMax');
  // An inverted range is not an empty result, it is a caller bug: it can only
  // come from a control that let the handles cross, and answering it with zero
  // conversations hides that.
  if (salaryMin !== undefined && salaryMax !== undefined && salaryMin > salaryMax) {
    throw new InboxQueryShapeError('salaryMin must not exceed salaryMax');
  }
  return {
    skills: optionalStrings(c.skills, 'skills'),
    languages: optionalStrings(c.languages, 'languages'),
    education: fromClosedSet(c.education, EDUCATION_LEVELS, 'education level'),
    preferredLocations: optionalStrings(c.preferredLocations, 'preferredLocations'),
    nationalities: fromClosedSet(c.nationalities, NATIONALITIES, 'nationality'),
    ...(salaryMin === undefined ? {} : { salaryMin }),
    ...(salaryMax === undefined ? {} : { salaryMax }),
  };
}

// ── lists ────────────────────────────────────────────────────────────────────
/**
 * A hand-picked conversation list as the BFF exchanges it. `channelIds` is the
 * stored member set (the upstream returns it as a JSON array); `icon` and
 * `color` are nullable — absent or explicitly null when the list has none.
 */
export interface ListDto {
  id: number;
  name: string;
  icon?: string | null;
  color?: string | null;
  position: number;
  channelIds: string[];
}

/** Create when `id` is absent, update the style (name/icon/color) when present. */
export interface SaveListBody {
  /** Absent when creating. */
  id?: number;
  name: string;
  icon?: string | null;
  color?: string | null;
  /** Honored only on create — the list's initial members. */
  channelIds?: string[];
}

/** Set the user's lists to this order of ids. */
export interface ReorderListsBody {
  ids: number[];
}

/** Add or remove members of a list — same shape for both. */
export interface ListMembersBody {
  id: number;
  channelIds: string[];
}

/**
 * The inbox's Unread pill ("All messages", open chats with a person, unread),
 * as the wire body — the one count the rail dot, widget badge, host navbar and
 * the BFF all report. `limit: 1` because only `total` is read.
 */
export const UNREAD_CHATS_QUERY: InboxQueryBody = {
  filters: [
    { kind: 'status', phases: ['open'] },
    { kind: 'kind', kinds: ['job', 'direct'] },
    { kind: 'scope', value: 'unread' },
  ],
  limit: 1,
};
